import { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Card, Form, InputGroup, Pagination, Table } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/common/PageHeader';
import Loader from '../components/common/Loader';
import EmptyState from '../components/common/EmptyState';
import PaymentBadge from '../components/patient/PaymentBadge';
import { patientApi } from '../api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { formatAge, formatDate, timeAgo, titleCase } from '../utils/format';

export default function Patients() {
  const navigate = useNavigate();
  const toast = useToast();
  const { can } = useAuth();

  const [search, setSearch] = useState('');
  const [term, setTerm] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ pages: 1, total: 0 });
  const [loading, setLoading] = useState(true);

  // Debounce so the list does not refetch on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      setTerm(search.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await patientApi.list({ search: term, page, limit: 20 });
      setData(res.data);
      setPagination(res.pagination);
    } catch (err) {
      toast.error(err);
    } finally {
      setLoading(false);
    }
  }, [term, page, toast]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <>
      <PageHeader
        title="All Patients"
        subtitle={`${pagination.total} patient${pagination.total === 1 ? '' : 's'} on record`}
        icon="bi-folder2-open"
      >
        {can('reception') && (
          <Button className="btn-clinic" onClick={() => navigate('/patients/new')}>
            <i className="bi bi-person-plus me-1" /> New Patient
          </Button>
        )}
      </PageHeader>

      <Card className="search-panel mb-4">
        <Card.Body>
          <InputGroup>
            <InputGroup.Text>
              <i className="bi bi-search" />
            </InputGroup.Text>
            <Form.Control
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, mobile number or patient ID"
            />
            {search && (
              <Button variant="outline-secondary" onClick={() => setSearch('')} title="Clear">
                <i className="bi bi-x-lg" />
              </Button>
            )}
          </InputGroup>
        </Card.Body>
      </Card>

      <Card>
        {loading ? (
          <Loader label="Loading patients..." />
        ) : data.length === 0 ? (
          <EmptyState
            icon="bi-person-x"
            title={term ? `No patient matches "${term}"` : 'No patients registered yet'}
            hint={term ? 'Try the mobile number instead.' : 'Register the first patient to get started.'}
          />
        ) : (
          <>
            <div className="table-responsive">
              <Table hover className="mb-0 align-middle">
                <thead>
                  <tr>
                    <th>Patient ID</th>
                    <th>Name</th>
                    <th>Mobile</th>
                    <th className="d-none d-md-table-cell">Age / Gender</th>
                    <th className="d-none d-lg-table-cell">Address</th>
                    <th>Visits</th>
                    <th>Payment</th>
                    <th>Last Visit</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((p) => (
                    <tr key={p._id} className="queue-row" onClick={() => navigate(`/patients/${p._id}`)}>
                      <td className="text-muted small font-monospace">{p.patientId}</td>
                      <td>
                        <div className="fw-semibold">{p.name}</div>
                        {p.allergies?.length > 0 && (
                          <div className="text-danger" style={{ fontSize: '0.75rem' }}>
                            <i className="bi bi-exclamation-triangle-fill me-1" />
                            {p.allergies.join(', ')}
                          </div>
                        )}
                      </td>
                      <td>{p.mobile}</td>
                      <td className="d-none d-md-table-cell text-muted small">
                        {formatAge(p)} &middot; {titleCase(p.gender)}
                      </td>
                      <td className="d-none d-lg-table-cell text-muted small" style={{ maxWidth: 200 }}>
                        {[p.address, p.city].filter(Boolean).join(', ') || '-'}
                      </td>
                      <td>
                        <Badge bg={p.totalVisits > 0 ? 'secondary' : 'light'} className={p.totalVisits ? '' : 'text-dark'}>
                          {p.totalVisits}
                        </Badge>
                      </td>
                      <td>
                        <PaymentBadge payment={p.payment} />
                      </td>
                      <td className="small">
                        {p.lastVisitDate ? (
                          <>
                            {formatDate(p.lastVisitDate)}
                            <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                              {timeAgo(p.lastVisitDate)}
                            </div>
                          </>
                        ) : (
                          <span className="text-muted">Never</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            {pagination.pages > 1 && (
              <Card.Footer className="d-flex justify-content-center bg-white">
                <Pagination className="mb-0">
                  <Pagination.Prev disabled={page === 1} onClick={() => setPage((p) => p - 1)} />
                  {Array.from({ length: Math.min(7, pagination.pages) }, (_, i) => {
                    const start = Math.max(1, Math.min(page - 3, pagination.pages - 6));
                    return start + i;
                  })
                    .filter((n) => n >= 1 && n <= pagination.pages)
                    .map((n) => (
                      <Pagination.Item key={n} active={n === page} onClick={() => setPage(n)}>
                        {n}
                      </Pagination.Item>
                    ))}
                  <Pagination.Next disabled={page >= pagination.pages} onClick={() => setPage((p) => p + 1)} />
                </Pagination>
              </Card.Footer>
            )}
          </>
        )}
      </Card>
    </>
  );
}
